/**
 * Agent export/import utilities - download a full agent config as a JSON
 * file, and parse one back into an AgentConfig for import.
 */

import type { AgentConfig } from '../types/agent';

const EXPORT_KIND = 'raiqon-agent-export';
const EXPORT_VERSION = 1;

export interface AgentExportFile {
  kind: typeof EXPORT_KIND;
  export_version: typeof EXPORT_VERSION;
  exported_at: string;
  agent: AgentConfig;
}

/**
 * Trigger a browser download of the given agent's full config as a JSON
 * file. Expects `agent` to already be the fully-resolved config (e.g. from
 * apiClient.getAgent), not the placeholder-filled summary from listAgents.
 */
export function downloadAgentExport(agent: AgentConfig): void {
  const payload: AgentExportFile = {
    kind: EXPORT_KIND,
    export_version: EXPORT_VERSION,
    exported_at: new Date().toISOString(),
    agent,
  };

  const filenameBase = agent.slug || slugifyForFilename(agent.name) || 'agent';
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenameBase}-agent-export.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function slugifyForFilename(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Parse and validate raw file text as an agent export. Throws a descriptive
 * Error for bad JSON, an unrecognized envelope, or a missing/malformed agent
 * so callers can surface the message directly to the user.
 */
export function parseAgentExportFile(raw: string): AgentConfig {
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('This file is not valid JSON.');
  }

  if (!parsed || typeof parsed !== 'object' || parsed.kind !== EXPORT_KIND) {
    throw new Error('This file is not a Raiqon agent export.');
  }
  if (parsed.export_version !== EXPORT_VERSION) {
    throw new Error(`Unsupported agent export version: ${parsed.export_version}`);
  }
  const agent = parsed.agent;
  if (!agent || typeof agent !== 'object' || !agent.name || !agent.task_type) {
    throw new Error('This export is missing required agent fields (name, task_type).');
  }

  return agent as AgentConfig;
}

/**
 * Return baseSlug if it's not already in existingSlugs, otherwise the first
 * "-2", "-3", ... suffixed variant that is free.
 */
export function dedupeSlug(baseSlug: string, existingSlugs: Set<string>): string {
  if (!existingSlugs.has(baseSlug)) return baseSlug;
  let i = 2;
  while (existingSlugs.has(`${baseSlug}-${i}`)) i++;
  return `${baseSlug}-${i}`;
}
